import { useState, useEffect, useCallback } from 'react';
import { Loader2, Send, AlertTriangle, Users, Save, CheckCircle } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '../../lib/utils';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Alert, AlertDescription } from '@easyflow/ui-frontend/components/ui/alert';

interface TelegramBot {
  id: string;
  bot_external_id: string;
  username: string | null;
  display_name: string;
  friendly_name: string | null;
  purpose: 'notifications' | 'messaging' | 'both';
  status: string;
  sender_id: string | null;
}

interface TenantUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
}

interface SenderAssignment {
  user_id: string;
  username?: string;
  email?: string;
}

interface SenderConfig {
  is_global: boolean;
  assignments: SenderAssignment[];
}

function userLabel(u: TenantUser): string {
  const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  return name || u.email;
}

export default function TelegramSettingsTab() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [bots, setBots] = useState<TelegramBot[]>([]);
  const [users, setUsers] = useState<TenantUser[]>([]);
  // sender_id → flag "disponibile a tutti" (mappa is_global)
  const [assignGlobal, setAssignGlobal] = useState<Record<string, boolean>>({});
  // sender_id → lista utenti assegnati (selezione multipla)
  const [assignData, setAssignData] = useState<Record<string, string[]>>({});
  const [savingSenderId, setSavingSenderId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [botsRes, usersRes] = await Promise.all([
        apiClient.get<{ bots: TelegramBot[] }>('/integrations/telegram/bots'),
        apiClient.get<TenantUser[]>('/messaging/sender-assignments/users'),
      ]);
      const botList = botsRes.data?.bots ?? [];
      const userList = usersRes.data ?? [];
      setBots(botList);
      setUsers(userList);

      const configs = await Promise.all(
        botList.map(async (b) => {
          if (!b.sender_id) return null;
          const cfg = await apiClient.get<SenderConfig>(`/messaging/senders/${b.sender_id}/assignments`);
          return { senderId: b.sender_id, config: cfg.data };
        }),
      );
      const globalMap: Record<string, boolean> = {};
      const dataMap: Record<string, string[]> = {};
      for (const entry of configs) {
        if (!entry || !entry.config) continue;
        globalMap[entry.senderId] = entry.config.is_global !== false;
        dataMap[entry.senderId] = entry.config.assignments.map((a) => a.user_id);
      }
      setAssignGlobal(globalMap);
      setAssignData(dataMap);
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Caricamento bot Telegram fallito', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleAssignUser = (senderId: string, userId: string) => {
    setAssignData((prev) => {
      const current = prev[senderId] ?? [];
      const updated = current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId];
      return { ...prev, [senderId]: updated };
    });
  };

  const handleSave = async (senderId: string) => {
    const previousGlobal = assignGlobal[senderId] !== false;
    const previousData = assignData[senderId] ?? [];
    setSavingSenderId(senderId);
    try {
      const isGlobal = assignGlobal[senderId] !== false;
      // Quando il bot è "disponibile a tutti" ignoriamo eventuali utenti
      // selezionati: la regola "Tutti" prevale (come in SMS/WhatsApp).
      const userIds = isGlobal ? [] : previousData;
      await apiClient.put(`/messaging/senders/${senderId}/assignments`, { user_ids: userIds, is_global: isGlobal });
      toast({ title: 'Salvato', description: 'Assegnazione aggiornata' });
    } catch (err) {
      setAssignGlobal((s) => ({ ...s, [senderId]: previousGlobal }));
      setAssignData((s) => ({ ...s, [senderId]: previousData }));
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Salvataggio assegnazione fallito', variant: 'destructive' });
    } finally {
      setSavingSenderId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Send className="w-5 h-5 text-sky-600" />
          Bot Telegram
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Per ogni bot scegli se è disponibile a tutti gli utenti del tenant oppure ristretto
          a una lista esplicita di assegnatari. La registrazione di nuovi bot avviene da
          Integrazioni → Telegram.
        </p>
      </div>

      {bots.length === 0 ? (
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            Nessun bot Telegram registrato. Aggiungine uno da Integrazioni → Telegram.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {bots.map((bot) => {
            const senderId = bot.sender_id;
            const hasSender = senderId !== null;
            const isGlobal = hasSender ? assignGlobal[senderId] !== false : true;
            const assigned = hasSender ? (assignData[senderId] ?? []) : [];
            const saving = hasSender && savingSenderId === senderId;
            return (
              <Card key={bot.id} className="border">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-medium text-gray-900 truncate">
                        {bot.friendly_name || bot.display_name}
                      </span>
                      {bot.username && <span className="text-sm text-gray-400 truncate">@{bot.username}</span>}
                      <Badge variant={bot.status === 'active' ? 'default' : 'secondary'}>{bot.status}</Badge>
                    </div>
                    {hasSender && (
                      <div className="flex items-center gap-2 shrink-0">
                        {isGlobal ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Tutti</Badge>
                        ) : (
                          <Badge variant="outline">{assigned.length} utent{assigned.length === 1 ? 'e' : 'i'}</Badge>
                        )}
                        <Button size="sm" onClick={() => handleSave(senderId)} disabled={saving}>
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                          <span className="ml-1">Salva</span>
                        </Button>
                      </div>
                    )}
                  </div>

                  {!hasSender ? (
                    <Alert>
                      <AlertTriangle className="w-4 h-4" />
                      <AlertDescription>
                        Bot non ancora attivabile per la messaggistica: invia o ricevi un messaggio
                        per creare il mittente, poi gestisci le assegnazioni.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-3 p-2 rounded-md bg-muted border">
                        <Switch
                          checked={isGlobal}
                          onCheckedChange={(v) => setAssignGlobal((prev) => ({ ...prev, [senderId]: v }))}
                        />
                        <div className="text-sm">
                          <div className="font-medium">Disponibile a tutti</div>
                          <div className="text-xs text-muted-foreground">
                            {isGlobal
                              ? 'Ogni utente del tenant può usare questo bot.'
                              : 'Solo gli utenti selezionati qui sotto possono usare questo bot.'}
                          </div>
                        </div>
                      </div>
                      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 ${isGlobal ? 'opacity-50 pointer-events-none' : ''}`}>
                        {users.map((u) => {
                          const isSelected = assigned.includes(u.id);
                          return (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => toggleAssignUser(senderId, u.id)}
                              disabled={isGlobal}
                              className={`flex items-center gap-2 p-2 rounded-md border text-sm transition-colors text-left ${
                                isSelected
                                  ? 'bg-blue-50 border-blue-300 text-blue-800'
                                  : 'bg-card border-border text-muted-foreground hover:bg-accent'
                              }`}
                            >
                              <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                                isSelected ? 'bg-blue-600 border-blue-600' : 'border-border'
                              }`}>
                                {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                              </div>
                              <div className="truncate">
                                <div className="font-medium truncate">{userLabel(u)}</div>
                                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
